import { APP_ROUTES, isDevMenuEnabled } from '@bundles/constant/routes'
import ProtectedRoute from '@bundles/layouts/ProtectedRoute'
import { lazy } from 'react'
import { matchPath } from 'react-router-dom'

// Lazy load components
const Order = lazy(() => import('@pages/Order'))
const OrderPrint = lazy(() => import('@pages/Order/OrderPrint'))
const ModalVariable = lazy(
  () => import('@themeForm/modal/ModalVariableContent'),
)
const CreateAutomationEmailModal = lazy(
  () => import('@pages/Automation/CreateModal'),
)
const SettingsTabsPage = lazy(() => import('@pages/Settings/SettingsTabsPage'))
const OrderPdf = lazy(() => import('@pages/Order/OrderPdf'))
const ThemeEdit = lazy(() => import('@pages/Themes/edit'))
const InvoiceGenerator = lazy(() => import('@pages/InvoiceGenerator'))
const InvoiceGeneratorDetail = lazy(
  () =>
    import(
      '@pages/InvoiceGenerator/InvoiceGeneratorCreate/InvoiceGeneratorCreate'
    ),
)
const InvoiceGeneratorCreateCustomer = lazy(
  () => import('@pages/InvoiceGenerator/ModalCreate/ModalCreateForm'),
)
const InvoiceGeneratorCreateProduct = lazy(
  () => import('@pages/InvoiceGenerator/ModalCreate/ModalCreateProductForm'),
)
const InvoiceGenerateConfirmDelete = lazy(
  () => import('@pages/InvoiceGenerator/ModalConfirm/ModalConfirmContent'),
)
const Downloads = lazy(() => import('@pages/Downloads'))
const Subscriptions = lazy(() => import('@pages/Subscriptions'))
const SubscriptionsCallback = lazy(
  () => import('@pages/Subscriptions/callback'),
)
const DashBoard = lazy(() => import('@pages/DashBoard'))
const RecommendedAppsPage = lazy(
  () => import('@features/recommended-apps/RecommendedAppsPage'),
)
const NewAutomationEmail = lazy(() => import('@pages/Automation/NewAutomation'))
const DetailAutomationEmail = lazy(() => import('@pages/Automation/Detail'))
const EditShopifyFlowEmail = lazy(
  () => import('@pages/Automation/ShopifyFlowEmails/Edit'),
)
const Translation = lazy(() => import('@pages/Translation/index'))
const Pos = lazy(() => import('@pages/Pos/index'))
// const OrderBulkPrint = lazy(() => import('@pages/Order/OrderBulkPrint'))
const DraftOrderPrint = lazy(() => import('@pages/Order/DraftOrder/print'))
const SendEmail = lazy(() => import('@pages/Order/SendEmail'))
const TemplateModal = lazy(() => import('@components/Modal/TemplateModal'))

const AccountSettings = lazy(() => import('@pages/Settings/AccountSettings'))
const SendDraftEmail = lazy(() => import('@pages/Order/DraftOrder/SendEmail'))
const EditCode = lazy(() => import('@pages/Themes/EditCode'))
const ThemeView = lazy(() => import('@pages/Themes/ThemeView'))
const ActivitiesLog = lazy(() => import('@pages/ActivitiesLog/ActivitiesLog'))
const TemplateGallery = lazy(() => import('@pages/Template/TemplateGallery'))
const TemplateCustomization = lazy(
  () => import('@pages/Template/TemplateCustomization'),
)
const DownloadFile = lazy(() => import('@pages/Order/Inc/DownloadFile'))

const QuoteDetail = lazy(() => import('@pages/Quote/QuoteDetail/QuoteDetail'))
const OrderDetail = lazy(() => import('@pages/Order/OrderDetail/OrderDetail'))

const OrderCustom = lazy(
  () => import('@bundles/pages/Order/order-custom/OrderCustom'),
)
const DraftOrderDetail = lazy(
  () => import('@pages/Order/DraftOrder/DraftOrderDetail'),
)
const ModalSendMailOrderDownload = lazy(
  () => import('@pages/Order/Modal/ModalInfoDownloadOrder'),
)
const PrintPdf = lazy(() => import('@pages/Order/OrderDetail/PrintPdf'))
const RedirectPrintPage = lazy(
  () => import('@components/RedirectPrintPage/RedirectPrintPage'),
)
const ReviewPage = lazy(() => import('@pages/Review/ReviewPage'))
const QuotePage = lazy(() => import('@pages/Quote/Quotes'))
const QuoteCreate = lazy(() => import('@pages/Quote/CreateForm'))
const ThemeTemplate = lazy(
  () => import('@fordeerTheme/components/modal/changeTemplate'),
)
const ReportPage = lazy(() => import('@pages/Report'))
const ReportCustomTheme = lazy(
  () => import('@pages/Report/CustomTheme/ReportCustomTheme'),
)
const DevPage = lazy(() => import('@pages/Dev'))

export type AppRouteConfig = {
  path: string
  component: React.ReactNode
  isDashboard?: boolean
  noFrame?: boolean
}

const rawAppRoutes: AppRouteConfig[] = [
  { path: APP_ROUTES.DASHBOARD, component: <DashBoard />, isDashboard: true },
  { path: APP_ROUTES.POS, component: <Pos /> },
  { path: APP_ROUTES.PARTNERS, component: <RecommendedAppsPage /> },
  { path: APP_ROUTES.ACTIVITIES_LOG, component: <ActivitiesLog /> },
  { path: APP_ROUTES.PRICING_PLANS, component: <AccountSettings /> },
  { path: `${APP_ROUTES.SETTINGS}/*`, component: <SettingsTabsPage /> },
  { path: APP_ROUTES.ORDERS, component: <Order /> },
  { path: APP_ROUTES.ORDER_DETAIL, component: <OrderDetail /> },
  { path: APP_ROUTES.ORDER_CUSTOM, component: <OrderCustom /> },
  { path: APP_ROUTES.ORDERS_DOWNLOAD_BY_LAYOUT, component: <OrderDetail /> },
  { path: APP_ROUTES.DRAFT_ORDER_CREATE, component: <DraftOrderDetail /> },
  { path: APP_ROUTES.DRAFT_ORDER_DETAIL, component: <DraftOrderDetail /> },
  {
    path: APP_ROUTES.ORDERS_SEND_EMAIL,
    component: <SendEmail />,
    noFrame: true,
  },
  {
    path: APP_ROUTES.ORDERS_DOWNLOAD_FILE,
    component: <DownloadFile />,
    noFrame: true,
  },
  { path: APP_ROUTES.DRAFT_ORDERS_PRINT, component: <DraftOrderPrint /> },
  {
    path: APP_ROUTES.DRAFT_ORDERS_SEND_EMAIL,
    component: <SendDraftEmail />,
    noFrame: true,
  },
  { path: APP_ROUTES.ORDERS_PRINT_BY_TASK_AND_ID, component: <OrderPrint /> },
  { path: APP_ROUTES.ORDERS_PRINT_BY_TASK, component: <OrderPrint /> },
  {
    path: APP_ROUTES.ORDERS_INLINE_PDF,
    component: <OrderPdf />,
    noFrame: true,
  },
  { path: APP_ROUTES.PDF_OUTPUT, component: <OrderPdf />, noFrame: true },
  {
    path: APP_ROUTES.MODAL_VARIABLES,
    component: <ModalVariable />,
    noFrame: true,
  },

  { path: APP_ROUTES.THEMES_EDIT, component: <ThemeEdit />, noFrame: true },
  { path: APP_ROUTES.THEMES_CODE, component: <EditCode />, noFrame: true },
  { path: APP_ROUTES.THEMES_VIEW, component: <ThemeView />, noFrame: true },
  {
    path: APP_ROUTES.THEMES_TEMPLATES,
    component: (
      <ThemeTemplate open={true} title="Theme Template" onClose={() => {}} />
    ),
  },
  { path: APP_ROUTES.SUBSCRIPTIONS, component: <Subscriptions /> },
  {
    path: APP_ROUTES.POPUP_SUBSCRIPTIONS,
    component: <Subscriptions />,
    noFrame: true,
  },
  {
    path: APP_ROUTES.MODAL_PRICING,
    component: <Subscriptions />,
    noFrame: true,
  },
  {
    path: APP_ROUTES.PRICING_CALLBACK,
    component: <SubscriptionsCallback />,
  },
  { path: APP_ROUTES.INVOICE_GENERATOR, component: <InvoiceGenerator /> },
  {
    path: APP_ROUTES.INVOICE_GENERATOR_CREATE,
    component: <InvoiceGeneratorDetail />,
  },
  {
    path: APP_ROUTES.INVOICE_GENERATOR_EDIT,
    component: <InvoiceGeneratorDetail />,
  },
  {
    path: APP_ROUTES.INVOICE_GENERATOR_CUSTOMER_CREATE,
    component: <InvoiceGeneratorCreateCustomer />,
    noFrame: true,
  },
  {
    path: APP_ROUTES.INVOICE_GENERATOR_PRODUCT_CREATE,
    component: <InvoiceGeneratorCreateProduct />,
    noFrame: true,
  },
  {
    path: APP_ROUTES.INVOICE_GENERATOR_DELETE_CONFIRM,
    component: <InvoiceGenerateConfirmDelete />,
    noFrame: true,
  },
  { path: APP_ROUTES.QUOTES_DETAIL, component: <QuoteDetail /> },
  { path: APP_ROUTES.QUOTES, component: <QuotePage /> },
  { path: APP_ROUTES.QUOTES_EDIT, component: <QuoteCreate /> },
  { path: APP_ROUTES.QUOTES_CREATE, component: <QuoteCreate /> },
  { path: APP_ROUTES.REPORTS, component: <ReportPage /> },
  {
    path: APP_ROUTES.REPORTS_CUSTOM_THEME,
    component: <ReportCustomTheme />,
    noFrame: true,
  },
  { path: APP_ROUTES.ORDERS_DOWNLOADS, component: <Downloads /> },
  { path: APP_ROUTES.AUTOMATIONS, component: <NewAutomationEmail /> },
  {
    path: APP_ROUTES.AUTOMATIONS_SHOPIFY_NOTI,
    component: <NewAutomationEmail />,
  },
  { path: APP_ROUTES.AUTOMATIONS_HISTORY, component: <NewAutomationEmail /> },
  {
    path: APP_ROUTES.AUTOMATIONS_MANUAL_EMAIL,
    component: <NewAutomationEmail />,
  },
  {
    path: APP_ROUTES.AUTOMATIONS_SHOPIFY_FLOW,
    component: <NewAutomationEmail />,
  },
  {
    path: APP_ROUTES.AUTOMATIONS_SHOPIFY_FLOW_EDIT,
    component: <EditShopifyFlowEmail />,
    noFrame: true,
  },
  {
    path: APP_ROUTES.AUTOMATIONS_CREATE,
    component: <CreateAutomationEmailModal />,
    noFrame: true,
  },
  {
    path: APP_ROUTES.AUTOMATIONS_EDIT,
    component: <DetailAutomationEmail />,
  },
  { path: APP_ROUTES.TRANSLATION, component: <Translation /> },
  { path: APP_ROUTES.TEMPLATE_GALLERY, component: <TemplateGallery /> },
  {
    path: APP_ROUTES.TEMPLATE_CUSTOMIZATION,
    component: <TemplateCustomization />,
  },
  {
    path: APP_ROUTES.MODAL_TEMPLATES,
    component: <TemplateModal />,
    noFrame: true,
  },
  {
    path: APP_ROUTES.ORDERS_MODAL_DOWNLOAD_SEND_MAIL,
    component: <ModalSendMailOrderDownload />,
    noFrame: true,
  },
  { path: APP_ROUTES.ORDERS_PRINT_PDF, component: <PrintPdf />, noFrame: true },
  {
    path: APP_ROUTES.PRINT_REDIRECT,
    component: <RedirectPrintPage />,
    noFrame: true,
  },
  { path: APP_ROUTES.REVIEW, component: <ReviewPage /> },
  ...(isDevMenuEnabled
    ? [{ path: APP_ROUTES.DEV, component: <DevPage /> }]
    : []),
]

export const appRoutes: AppRouteConfig[] = rawAppRoutes.map(route => ({
  ...route,
  component: (
    <ProtectedRoute noFrame={route.noFrame}>{route.component}</ProtectedRoute>
  ),
}))

export const isNoFramePath = (pathname: string): boolean =>
  appRoutes.some(
    route =>
      route.noFrame &&
      matchPath({ path: route.path, end: true }, pathname) != null,
  )
